import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { t } from 'i18next';
import { useSnackbar } from 'notistack';
import CalendarPicker from './CalendarPicker';
import Popover from '@mui/material/Popover';
import Modal from '@mui/material/Modal';
import Backdrop from '@mui/material/Backdrop';
import Fade from '@mui/material/Fade';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFilter, faTableColumns, faEllipsisVertical, faXmark,
  faGripVertical, faFileExport, faPenToSquare, faTrashCan,
} from '@fortawesome/free-solid-svg-icons';
import EmptyState from './EmptyState';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface ColDef<T = any> {
  key: string;
  label: string;
  align?: 'left' | 'right' | 'center';
  width?: string | number;               // grid-трек: число → px, строка ('1.5fr','90px','5%'); пусто → 1fr
  sortable?: boolean;                    // default true
  sortExtract?: (row: T) => any;         // значение для сортировки
  sortValue?: (row: T) => any;           // алиас sortExtract
  render: (row: T) => React.ReactNode;
  filterType?: 'text' | 'select' | 'boolean' | 'daterange';
  filterOptions?: { value: string; label: string }[];
  filterExtract?: (row: T) => string;    // fallback: String(row[key])
  multiSelect?: boolean;                 // when true + filterType='select': multi-value filter with search
  defaultVisible?: boolean;              // default true
  hideable?: boolean;                    // default true
}

export interface BulkEditField {
  key: string;
  label: string;
  type: 'select' | 'text' | 'boolean';
  options?: { value: any; label: string }[];
}

export interface MenuAction {
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
  divider?: boolean;
}

interface ActiveFilter { colKey: string; label: string; value: string; valueTo?: string; values?: string[]; }

export interface DataTableProps<T = any> {
  rows: T[];
  columns: ColDef<T>[];
  getKey: (row: T) => string | number;

  onRowClick?: (row: T) => void;
  onRowDoubleClick?: (row: T) => void;
  onEdit?: (row: T) => void;
  /** Скрыть иконку редактирования у конкретных строк (например, у уже одобренных) */
  canEdit?: (row: T) => boolean;
  onDelete?: (row: T) => void;
  canDelete?: (row: T) => boolean;
  selectedKey?: string | number | null;
  renderExpanded?: (row: T) => React.ReactNode;

  // Множественный выбор / bulk
  onDeleteMany?: (keys: (string | number)[]) => Promise<void>;
  bulkEditFields?: BulkEditField[];
  onBulkEdit?: (keys: (string | number)[], field: string, value: any) => Promise<void>;

  // Включение/выключение фич (булевы флаги)
  pagination?: boolean;                  // футер-пагинация (default true)
  filterable?: boolean;                 // кнопка Filter + фильтры (default true)
  selectable?: boolean;                 // чекбоксы множественного выбора (default: есть bulk-хендлеры)
  columnsToggle?: boolean;              // кнопка Columns (default true)
  exportable?: boolean;                 // экспорт CSV (default true)

  pageSizes?: number[];                  // default [25,50,100]
  defaultPageSize?: number;              // default 50

  defaultSortKey?: string;
  emptyText?: string;
  storageKey?: string;                   // сохранение видимости/порядка колонок
  csvFilename?: string;
  topLeft?: React.ReactNode;
  topRight?: React.ReactNode;
  menuActions?: MenuAction[];
  toolbarRef?: React.RefObject<HTMLElement>;
  hideToolbar?: boolean;
  /** Без внешней карточки (родитель предоставляет контейнер) */
  noCard?: boolean;
  /** @deprecated не используется */
  footerIcon?: React.ReactNode;
}

const trackOf = (w?: string | number) =>
  w === undefined || w === null ? 'minmax(0,1fr)' : typeof w === 'number' ? `${w}px` : w;

function loadStorage(key?: string): { order?: string[]; hidden?: string[] } {
  if (!key) return {};
  try { return JSON.parse(localStorage.getItem(key) ?? '{}'); } catch { return {}; }
}
function saveStorage(key: string | undefined, order: string[], hidden: Set<string>) {
  if (!key) return;
  try { localStorage.setItem(key, JSON.stringify({ order, hidden: Array.from(hidden) })); } catch {}
}

// ── Component ───────────────────────────────────────────────────────────────

export default function DataTable<T = any>({
  rows, columns, getKey,
  onRowClick, onRowDoubleClick, onEdit, canEdit, onDelete, canDelete, selectedKey, renderExpanded,
  onDeleteMany, bulkEditFields, onBulkEdit,
  pagination = true, filterable = true, selectable, columnsToggle = true, exportable = true,
  pageSizes = [25, 50, 100], defaultPageSize = 50,
  defaultSortKey, emptyText, storageKey, csvFilename = 'export.csv',
  topLeft, topRight, menuActions, toolbarRef, hideToolbar, noCard,
}: DataTableProps<T>) {
  const selectionEnabled = selectable ?? (!!onDeleteMany || !!onBulkEdit);
  const hasActions = !!onEdit || !!onDelete;

  // Portal readiness
  const [portalReady, setPortalReady] = useState(false);
  useLayoutEffect(() => { if (toolbarRef?.current) setPortalReady(true); }, [toolbarRef]);

  // Expand
  const [expandedKey, setExpandedKey] = useState<string | number | null>(null);
  const toggleExpand = (key: string | number) => setExpandedKey(prev => prev === key ? null : key);

  // Column order/visibility
  const [colOrder, setColOrder] = useState<string[]>(() => {
    const s = loadStorage(storageKey);
    return s.order ?? columns.map(c => c.key);
  });
  const [hiddenCols, setHiddenCols] = useState<Set<string>>(() => {
    const s = loadStorage(storageKey);
    return new Set(s.hidden ?? columns.filter(c => c.defaultVisible === false).map(c => c.key));
  });
  const persistCols = useCallback((order: string[], hidden: Set<string>) => saveStorage(storageKey, order, hidden), [storageKey]);

  const columnKeys = columns.map(c => c.key).join(',');
  useEffect(() => {
    setColOrder(prev => {
      const set = new Set(prev);
      const added = columns.map(c => c.key).filter(k => !set.has(k));
      if (!added.length) return prev;
      return [...prev, ...added];
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnKeys]);

  const toggleCol = useCallback((key: string) => {
    setHiddenCols(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      persistCols(colOrder, next);
      return next;
    });
  }, [colOrder, persistCols]);

  const [pickerAnchor, setPickerAnchor] = useState<HTMLElement | null>(null);
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const handleDrop = useCallback((toIdx: number) => {
    if (dragIdx === null || dragIdx === toIdx) return;
    setColOrder(prev => {
      const next = [...prev];
      const [moved] = next.splice(dragIdx, 1);
      next.splice(toIdx, 0, moved);
      persistCols(next, hiddenCols);
      return next;
    });
    setDragIdx(null);
  }, [dragIdx, hiddenCols, persistCols]);

  // Sort
  const autoKey = useMemo(() => {
    if (defaultSortKey) return defaultSortKey;
    const nameCol = columns.find(c =>
      c.sortable !== false &&
      (c.key === 'name' || /(^|_)(name|title)$/i.test(c.key) || /\b(name|имя|ім'я|title|назв)/i.test(c.label || ''))
    );
    return nameCol?.key ?? '';
  }, [columns, defaultSortKey]);
  const [sortKey, setSortKey] = useState<string>(autoKey);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  // Filters
  const [filters, setFilters] = useState<ActiveFilter[]>([]);
  const [filterAnchor, setFilterAnchor] = useState<HTMLElement | null>(null);
  const [filterCol, setFilterCol] = useState('');
  const [filterVal, setFilterVal] = useState('');
  const [filterValTo, setFilterValTo] = useState('');
  const filterableCols = useMemo(() => columns.filter(c => !!c.filterType), [columns]);

  // Pagination
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(defaultPageSize > 0 ? defaultPageSize : 50);

  // Selection / bulk
  const [selected, setSelected] = useState<(string | number)[]>([]);
  const [bulkEditAnchor, setBulkEditAnchor] = useState<HTMLElement | null>(null);
  const [bulkField, setBulkField] = useState('');
  const [bulkValueStr, setBulkValueStr] = useState('');
  const [bulkLoading, setBulkLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const { enqueueSnackbar } = useSnackbar();

  const addFilter = useCallback(() => {
    const col = columns.find(c => c.key === filterCol);
    if (!col) return;
    if (col.filterType === 'daterange') {
      if (!filterVal && !filterValTo) return;
      const fmt = (d: string) => d ? new Date(d + 'T00:00:00').toLocaleDateString() : '...';
      const label = `${col.label}: ${fmt(filterVal)} — ${fmt(filterValTo)}`;
      setFilters(prev => [...prev.filter(f => f.colKey !== filterCol), { colKey: filterCol, label, value: filterVal, valueTo: filterValTo }]);
    } else {
      if (!filterVal) return;
      const optLabel = col.filterOptions?.find(o => o.value === filterVal)?.label;
      const label = (col.filterType === 'select' || col.filterType === 'boolean') && optLabel
        ? `${col.label}: ${optLabel}` : `${col.label}: ${filterVal}`;
      setFilters(prev => [...prev.filter(f => f.colKey !== filterCol), { colKey: filterCol, label, value: filterVal }]);
    }
    setFilterAnchor(null); setFilterVal(''); setFilterValTo(''); setPage(0);
  }, [columns, filterCol, filterVal, filterValTo]);

  const removeFilter = useCallback((colKey: string) => {
    setFilters(prev => prev.filter(f => f.colKey !== colKey)); setPage(0);
  }, []);

  // Computed
  const filteredRows = useMemo(() => {
    if (!filters.length) return rows;
    return rows.filter(row => filters.every(f => {
      const col = columns.find(c => c.key === f.colKey);
      if (!col) return true;
      const val = col.filterExtract ? col.filterExtract(row) : String((row as any)[f.colKey] ?? '');
      if (col.filterType === 'daterange') {
        if (!val) return !f.value && !f.valueTo;
        const rt = new Date(val).getTime();
        if (f.value && rt < new Date(f.value + 'T00:00:00').getTime()) return false;
        if (f.valueTo && rt > new Date(f.valueTo + 'T23:59:59').getTime()) return false;
        return true;
      }
      if (col.filterType === 'select' || col.filterType === 'boolean') {
        if (f.values?.length) return f.values.includes(val);
        return val === f.value;
      }
      return val.toLowerCase().includes(f.value.toLowerCase());
    }));
  }, [rows, filters, columns]);

  const sorted = useMemo(() => {
    if (!sortKey) return filteredRows;
    const col = columns.find(c => c.key === sortKey);
    const acc = (r: T) => col?.sortExtract ? col.sortExtract(r) : col?.sortValue ? col.sortValue(r) : (r as any)[sortKey] ?? '';
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...filteredRows].sort((a, b) => {
      const va = acc(a), vb = acc(b);
      if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir;
      return String(va).localeCompare(String(vb)) * dir;
    });
  }, [filteredRows, sortKey, sortDir, columns]);

  const totalPages = pagination ? Math.max(1, Math.ceil(sorted.length / pageSize)) : 1;
  useEffect(() => { if (page > totalPages - 1) setPage(0); }, [totalPages, page]);
  const visibleRows = pagination ? sorted.slice(page * pageSize, (page + 1) * pageSize) : sorted;

  const visibleCols = useMemo(
    () => colOrder.filter(k => !hiddenCols.has(k)).map(k => columns.find(c => c.key === k)!).filter(Boolean),
    [colOrder, hiddenCols, columns],
  );

  const template = useMemo(() => {
    const parts: string[] = [];
    if (selectionEnabled) parts.push('40px');
    visibleCols.forEach(c => parts.push(trackOf(c.width)));
    if (hasActions) parts.push('90px');
    if (renderExpanded) parts.push('40px');
    return parts.join(' ');
  }, [selectionEnabled, visibleCols, hasActions, renderExpanded]);

  const toggleSort = (key: string) => {
    if (sortKey === key) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('asc'); }
    setPage(0);
  };
  const arrow = (key: string) => (sortKey === key ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '');

  // Selection helpers
  const allSelected = filteredRows.length > 0 && filteredRows.every(r => selected.includes(getKey(r)));
  const someSelected = selected.length > 0 && !allSelected;
  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) =>
    setSelected(e.target.checked ? filteredRows.map(r => getKey(r)) : []);
  const toggleRow = (key: string | number) =>
    setSelected(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!onDeleteMany) return;
    setDeleting(true);
    try {
      await onDeleteMany(selected);
      setSelected([]);
    } catch {
      enqueueSnackbar(t('Error') as string, { variant: 'error' });
    } finally {
      setDeleting(false);
      setConfirmOpen(false);
    }
  }, [onDeleteMany, selected, enqueueSnackbar]);

  const handleExportCsv = useCallback(() => {
    const headers = visibleCols.map(c => `"${c.label}"`).join(',');
    const csvRows = sorted.map(row => visibleCols.map(col => {
      const raw = col.filterExtract ? col.filterExtract(row) : String((row as any)[col.key] ?? '');
      return `"${raw.replace(/"/g, '""')}"`;
    }).join(','));
    const csv = '﻿' + [headers, ...csvRows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = csvFilename; a.click();
    URL.revokeObjectURL(url);
    setMenuAnchor(null);
  }, [visibleCols, sorted, csvFilename]);

  const pageNums = useMemo(() => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1) as (number | null)[];
    const cur = page + 1, delta = 2;
    const start = Math.max(2, cur - delta), end = Math.min(totalPages - 1, cur + delta);
    const nums: (number | null)[] = [1];
    if (start > 2) nums.push(null);
    for (let p = start; p <= end; p++) nums.push(p);
    if (end < totalPages - 1) nums.push(null);
    if (totalPages > 1) nums.push(totalPages);
    return nums;
  }, [page, totalPages]);

  const hasMenu = exportable || (menuActions && menuActions.length > 0);
  const controlButtons = (
    <>
      {filterable && filterableCols.length > 0 && (
        <button type="button" title="Add filter" className="dt-btn-ghost"
          onClick={e => { setFilterCol(filterableCols[0]?.key ?? ''); setFilterVal(''); setFilterAnchor(e.currentTarget); }}>
          <FontAwesomeIcon icon={faFilter} style={{ fontSize: 11, opacity: 0.6 }} /> + {t('Filter')}
        </button>
      )}
      {topRight && <>{topRight}<div className="toolbar-sep" /></>}
      {columnsToggle && (
        <button type="button" title="Columns" className="dt-btn-ghost" onClick={e => setPickerAnchor(e.currentTarget)}>
          <FontAwesomeIcon icon={faTableColumns} style={{ fontSize: 11, opacity: 0.6 }} /> {t('Columns')}
        </button>
      )}
      {hasMenu && (
        <button type="button" title="More options" className="dt-btn-ghost" onClick={e => setMenuAnchor(e.currentTarget)}>
          <FontAwesomeIcon icon={faEllipsisVertical} style={{ fontSize: 13, opacity: 0.6 }} />
        </button>
      )}
    </>
  );

  return (
    <div className={noCard ? 'scroll-table' : 'scroll-table ts-card'}>
      {/* Controls */}
      {!hideToolbar && (
        toolbarRef ? (
          <>
            {topLeft && <div className="dt-toolbar-row" data-controls=""><div className="flex items-center gap-[8px] flex-wrap flex-1 min-w-0">{topLeft}</div></div>}
            {portalReady && toolbarRef.current && ReactDOM.createPortal(<>{controlButtons}</>, toolbarRef.current)}
          </>
        ) : (
          <div className="dt-toolbar-row" data-controls="">
            {topLeft && <div className="flex items-center gap-[8px] flex-wrap flex-1 min-w-0">{topLeft}</div>}
            <div style={{ flex: 1 }} />
            {controlButtons}
          </div>
        )
      )}

      {/* Filter chips */}
      {filters.length > 0 && (
        <div className="flex items-center gap-[6px] mb-[6px] flex-wrap" style={{ flexShrink: 0 }}>
          {filters.map(f => (
            <div key={f.colKey} className="flex items-center gap-[5px] text-[12px] px-[8px] py-[3px] rounded-full"
              style={{ backgroundColor: 'rgba(25,118,210,0.15)', border: '1px solid rgba(25,118,210,0.35)', color: 'var(--text-color)' }}>
              <span>{f.label}</span>
              <button type="button" title="Remove" onClick={() => removeFilter(f.colKey)} className="opacity-50 hover:opacity-80 ml-[1px]">
                <FontAwesomeIcon icon={faXmark} className="text-[10px]" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Header block */}
      <div className="st-head" style={{ gridTemplateColumns: template }}>
        {selectionEnabled && (
          <div className="st-hcell" style={{ padding: '0 4px', display: 'flex', alignItems: 'center' }}>
            <Checkbox color="primary" size="small" indeterminate={someSelected} checked={allSelected} onChange={handleSelectAll} />
          </div>
        )}
        {visibleCols.map(c => (
          <div key={c.key} className="st-hcell"
            style={{ textAlign: c.align ?? 'left', cursor: c.sortable === false ? 'default' : 'pointer' }}
            onClick={() => c.sortable !== false && toggleSort(c.key)}>
            {c.label}{c.sortable !== false ? arrow(c.key) : ''}
          </div>
        ))}
        {hasActions && <div className="st-hcell" style={{ textAlign: 'right' }}>{t('Edit')}</div>}
        {renderExpanded && <div className="st-hcell" />}
      </div>

      {/* Body */}
      <div className="st-body thin-scroll">
        {sorted.length === 0 ? (
          <EmptyState message={emptyText ?? (t('Empty') as string)} />
        ) : (
          visibleRows.map(row => {
            const key = getKey(row);
            const isSel = selectedKey != null && key === selectedKey;
            const isChecked = selected.includes(key);
            const deletable = canDelete ? canDelete(row) : true;
            const editable = canEdit ? canEdit(row) : true;
            const isExpanded = renderExpanded && expandedKey === key;
            return (
              <React.Fragment key={key}>
                <div className={`st-row${isSel ? ' is-selected' : ''}`}
                  style={{ gridTemplateColumns: template, cursor: (onRowClick || onRowDoubleClick || renderExpanded) ? 'pointer' : 'default' }}
                  onClick={() => { if (renderExpanded) toggleExpand(key); else onRowClick?.(row); }}
                  onDoubleClick={() => !renderExpanded && (onRowDoubleClick ?? onEdit)?.(row)}>
                  {selectionEnabled && (
                    <div className="st-cell" style={{ padding: '0 4px', display: 'flex', alignItems: 'center' }} onClick={e => e.stopPropagation()}>
                      <Checkbox color="primary" size="small" checked={isChecked} onChange={() => toggleRow(key)} />
                    </div>
                  )}
                  {visibleCols.map(c => (
                    <div key={c.key} className="st-cell" style={{ textAlign: c.align ?? 'left' }}>{c.render(row)}</div>
                  ))}
                  {hasActions && (
                    <div className="st-cell" style={{ textAlign: 'right' }} onClick={e => e.stopPropagation()}>
                      <div className="flex gap-[7px] justify-end items-center">
                        {onEdit && editable && <FontAwesomeIcon icon={faPenToSquare} className="text-[14px] cursor-pointer opacity-50 hover:opacity-70 text-[var(--text-color)]" onClick={() => onEdit(row)} />}
                        {onDelete && <FontAwesomeIcon icon={faTrashCan} className={`text-[14px] opacity-50 text-[var(--text-color)] ${deletable ? 'cursor-pointer hover:opacity-70' : 'cursor-not-allowed'}`} onClick={() => deletable && onDelete(row)} />}
                      </div>
                    </div>
                  )}
                  {renderExpanded && (
                    <div className="st-cell" style={{ textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                      <button type="button" onClick={() => toggleExpand(key)}
                        style={{ width: 20, height: 20, borderRadius: 6, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', border: '1px solid var(--border-input)', background: isExpanded ? 'rgba(37,99,235,.1)' : 'var(--btn-user-form)', color: isExpanded ? '#2563eb' : 'var(--text-color-calendar-week)', fontSize: 10, cursor: 'pointer', transition: 'all .2s', transform: isExpanded ? 'rotate(180deg)' : 'none' }}>▾</button>
                    </div>
                  )}
                </div>
                {isExpanded && (
                  <div style={{ borderBottom: 'var(--border-input)' }}>
                    <div className="eq-expand-body">{renderExpanded!(row)}</div>
                  </div>
                )}
              </React.Fragment>
            );
          })
        )}
      </div>

      {/* Footer */}
      <div className="st-foot">
        {selectionEnabled ? (
          <div className="flex items-center gap-[10px]">
            <span className="st-foot-count">{selected.length} / {filteredRows.length}</span>
            {selected.length > 0 && bulkEditFields && bulkEditFields.length > 0 && onBulkEdit && (
              <button type="button" onClick={e => { setBulkField(''); setBulkValueStr(''); setBulkEditAnchor(e.currentTarget); }}
                className="flex items-center gap-[5px] text-[12px] px-[8px] py-[2px] rounded-[5px] text-[var(--text-color)] hover:opacity-80 whitespace-nowrap"
                style={{ border: '1px solid rgba(var(--shodow),0.25)' }}>
                <FontAwesomeIcon icon={faPenToSquare} className="opacity-50 text-[10px]" /><span className="opacity-70">{t('EditFields')}</span>
              </button>
            )}
            {selected.length > 0 && onDeleteMany && (
              <button type="button" title={t('Delete') as string} onClick={() => setConfirmOpen(true)}
                className="flex items-center justify-center w-[26px] h-[26px] rounded-[5px] hover:opacity-80"
                style={{ border: '1px solid rgba(var(--shodow),0.25)' }}>
                <FontAwesomeIcon icon={faTrashCan} className="text-[13px] opacity-50 text-[var(--text-color)]" />
              </button>
            )}
          </div>
        ) : (
          <span className="st-foot-count">{sorted.length}</span>
        )}

        {pagination && (
          <div className="st-foot-right">
            {totalPages > 1 && (
              <div className="st-pager">
                {pageNums.map((p, i) => p === null
                  ? <span key={`e${i}`} className="st-pager-gap">…</span>
                  : <button key={p} type="button" className={`dt-pg-btn${p === page + 1 ? ' active' : ''}`} onClick={() => setPage(p - 1)}>{p}</button>
                )}
              </div>
            )}
            <select className="st-foot-size" value={pageSize} title="Rows per page"
              onChange={e => { setPageSize(Number(e.target.value)); setPage(0); }}>
              {pageSizes.map(s => <option key={s} value={s}>{s} / page</option>)}
            </select>
          </div>
        )}
      </div>

      {/* More menu */}
      <Popover open={Boolean(menuAnchor)} anchorEl={menuAnchor} onClose={() => setMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ elevation: 0, sx: { mt: '6px', bgcolor: 'transparent', border: 'none', overflow: 'visible' } }}>
        <div className="dt-dropdown">
          {exportable && (
            <button type="button" className="dt-dropdown-item" onClick={handleExportCsv}>
              <FontAwesomeIcon icon={faFileExport} style={{ opacity: 0.5, width: 14 }} /> Export CSV
            </button>
          )}
          {menuActions?.map((a, i) => (
            <React.Fragment key={i}>
              {a.divider && <div style={{ height: 1, backgroundColor: 'var(--border-input)', margin: '3px 0' }} />}
              <button type="button" className="dt-dropdown-item" onClick={() => { a.onClick(); setMenuAnchor(null); }}>
                {a.icon && <span style={{ opacity: 0.5, width: 14, display: 'flex', alignItems: 'center' }}>{a.icon}</span>}{a.label}
              </button>
            </React.Fragment>
          ))}
        </div>
      </Popover>

      {/* Column picker */}
      <Popover open={Boolean(pickerAnchor)} anchorEl={pickerAnchor} onClose={() => setPickerAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ elevation: 0, sx: { mt: '6px', bgcolor: 'transparent', border: 'none', overflow: 'visible' } }}>
        <div className="dt-dropdown" style={{ width: 220 }}>
          <div style={{ padding: '10px 14px 8px', fontSize: 10, fontWeight: 800, letterSpacing: '.09em', textTransform: 'uppercase', color: 'var(--text-color-calendar-week)', borderBottom: '1px solid var(--border-input)' }}>{t('Columns')}</div>
          <div style={{ padding: '6px 8px 8px', display: 'flex', flexDirection: 'column', gap: 1 }}>
            {colOrder.map((key, i) => {
              const col = columns.find(c => c.key === key);
              if (!col || col.hideable === false) return null;
              const visible = !hiddenCols.has(key);
              return (
                <div key={key} draggable onDragStart={() => setDragIdx(i)} onDragOver={e => e.preventDefault()} onDrop={() => handleDrop(i)} onDragEnd={() => setDragIdx(null)} onClick={() => toggleCol(key)}
                  className="hover:bg-[var(--btn-user-form)]"
                  style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', borderRadius: 8, cursor: 'pointer', fontSize: 12, color: visible ? 'var(--text-color)' : 'var(--text-color-calendar-week)', background: dragIdx === i ? 'var(--btn-user-form)' : 'transparent' }}>
                  <FontAwesomeIcon icon={faGripVertical} style={{ opacity: 0.25, fontSize: 11, color: 'var(--text-color)', cursor: 'grab' }} />
                  <div style={{ width: 15, height: 15, borderRadius: 4, flexShrink: 0, border: `1.5px solid ${visible ? '#7c3aed' : 'var(--border-input)'}`, background: visible ? '#7c3aed' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {visible && <span style={{ fontSize: 9, color: 'white', fontWeight: 900 }}>✓</span>}
                  </div>
                  <span>{col.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      </Popover>

      {/* Filter popover */}
      <Popover open={Boolean(filterAnchor)} anchorEl={filterAnchor} onClose={() => setFilterAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }} transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: { bgcolor: 'var(--bg-form)', border: 'var(--border-input)', borderRadius: '6px', minWidth: 240 } }}>
        <div className="p-[12px] flex flex-col gap-[8px]">
          <div className="text-[11px] uppercase tracking-widest opacity-40 text-[var(--text-color)]">+ {t('Filter')}</div>
          <div>
            <label className="text-[11px] opacity-50 text-[var(--text-color)] mb-[2px] block">{t('Field')}</label>
            <select value={filterCol} onChange={e => { setFilterCol(e.target.value); setFilterVal(''); setFilterValTo(''); }} className="w-full text-[13px] py-[4px] px-[6px] rounded-[4px] timesheet-edit-input">
              {filterableCols.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </div>
          {(() => {
            const col = columns.find(c => c.key === filterCol);
            if (!col) return null;
            const isRange = col.filterType === 'daterange';
            const disabled = isRange ? (!filterVal && !filterValTo) : !filterVal;
            return (
              <>
                {isRange ? (
                  <div className="flex flex-col gap-[6px]">
                    <div><label className="text-[11px] opacity-50 text-[var(--text-color)] mb-[2px] block">{t('From')}</label>
                      <CalendarPicker mode="single" variant="bare" output="string" value={filterVal} onChange={(v) => setFilterVal(v)} className="w-full text-[13px] py-[4px] px-[6px] rounded-[4px] timesheet-edit-input" /></div>
                    <div><label className="text-[11px] opacity-50 text-[var(--text-color)] mb-[2px] block">{t('To')}</label>
                      <CalendarPicker mode="single" variant="bare" output="string" value={filterValTo} onChange={(v) => setFilterValTo(v)} className="w-full text-[13px] py-[4px] px-[6px] rounded-[4px] timesheet-edit-input" /></div>
                  </div>
                ) : (
                  <div>
                    <label className="text-[11px] opacity-50 text-[var(--text-color)] mb-[2px] block">{t('Value')}</label>
                    {(col.filterType === 'select' || col.filterType === 'boolean') ? (
                      <select value={filterVal} onChange={e => setFilterVal(e.target.value)} className="w-full text-[13px] py-[4px] px-[6px] rounded-[4px] timesheet-edit-input">
                        <option value="">—</option>
                        {[...(col.filterOptions ?? [])].sort((a, b) => a.label.localeCompare(b.label)).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    ) : (
                      <input autoFocus value={filterVal} onChange={e => setFilterVal(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') addFilter(); }} placeholder={t('Search') + '...'} className="w-full text-[13px] py-[4px] px-[6px] rounded-[4px] timesheet-edit-input" />
                    )}
                  </div>
                )}
                <button type="button" className="btn-primary" style={{ fontSize: 12 }} onClick={addFilter} disabled={disabled}>+ {t('Add')}</button>
              </>
            );
          })()}
        </div>
      </Popover>

      {/* Bulk edit popover */}
      <Popover open={Boolean(bulkEditAnchor)} anchorEl={bulkEditAnchor} onClose={() => setBulkEditAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }} transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        PaperProps={{ sx: { bgcolor: 'var(--bg-form)', border: 'var(--border-input)', borderRadius: '8px', minWidth: 260, boxShadow: 4 } }}>
        <div className="p-[14px] flex flex-col gap-[10px]">
          <div className="text-[11px] uppercase tracking-widest opacity-40 text-[var(--text-color)]">{t('EditFields')} · {selected.length} {t('Selected')}</div>
          <div>
            <label className="text-[12px] text-[var(--text-color)] opacity-60 block mb-[4px]">{t('Field')}</label>
            <select value={bulkField} onChange={e => { setBulkField(e.target.value); setBulkValueStr(''); }} className="w-full text-[13px] py-[5px] px-[8px] rounded-[5px] timesheet-edit-input">
              <option value="">— {t('SelectField')} —</option>
              {bulkEditFields?.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[12px] text-[var(--text-color)] opacity-60 block mb-[4px]">{t('Value')}</label>
            {!bulkField ? (
              <input disabled placeholder="—" className="w-full text-[13px] py-[5px] px-[8px] rounded-[5px] timesheet-edit-input opacity-30 cursor-not-allowed" />
            ) : (() => {
              const field = bulkEditFields?.find(f => f.key === bulkField);
              if (!field) return null;
              if (field.type === 'text') return <input type="text" value={bulkValueStr} onChange={e => setBulkValueStr(e.target.value)} className="w-full text-[13px] py-[5px] px-[8px] rounded-[5px] timesheet-edit-input" placeholder={field.label} />;
              return (
                <select value={bulkValueStr} onChange={e => setBulkValueStr(e.target.value)} className="w-full text-[13px] py-[5px] px-[8px] rounded-[5px] timesheet-edit-input">
                  <option value="">— {t('Select')} —</option>
                  {field.options?.map(o => <option key={String(o.value)} value={String(o.value)}>{o.label}</option>)}
                </select>
              );
            })()}
          </div>
          <Button variant="contained" size="small" disabled={!bulkField || bulkValueStr === '' || bulkLoading} sx={{ fontSize: 13, mt: '2px' }}
            onClick={async () => {
              if (!onBulkEdit || !bulkField || bulkValueStr === '') return;
              const field = bulkEditFields?.find(f => f.key === bulkField);
              if (!field) return;
              setBulkLoading(true);
              try {
                let value: any = bulkValueStr;
                if (field.type === 'boolean') value = bulkValueStr === 'true';
                else if (field.type === 'select') { const o = field.options?.find(x => String(x.value) === bulkValueStr); value = o ? o.value : bulkValueStr; }
                await onBulkEdit(selected, bulkField, value);
                setBulkEditAnchor(null); setSelected([]);
              } finally { setBulkLoading(false); }
            }}>
            {bulkLoading ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : `${t('Apply')} (${selected.length})`}
          </Button>
        </div>
      </Popover>

      {/* Bulk delete confirm */}
      <Modal open={confirmOpen} onClose={() => !deleting && setConfirmOpen(false)} closeAfterTransition slots={{ backdrop: Backdrop }} slotProps={{ backdrop: { timeout: 500 } }}>
        <Fade in={confirmOpen}>
          <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 400, bgcolor: 'var(--bg-form)', border: 'var(--border-input)', borderRadius: '5px', boxShadow: 24, p: 4 }}>
            <div className="text-center text-[16px] mb-[16px] text-[var(--text-color)]">{t('AreYouSure')}? {selected.length} {t('Selected')}</div>
            <div className="flex gap-[10px]">
              <Button variant="contained" sx={{ flex: 1, fontSize: 13 }} disabled={deleting} onClick={handleDeleteConfirm}>{deleting ? <CircularProgress size={18} color="inherit" /> : t('Yes')}</Button>
              <Button variant="contained" sx={{ flex: 1, fontSize: 13 }} disabled={deleting} onClick={() => setConfirmOpen(false)}>{t('Cancel')}</Button>
            </div>
          </Box>
        </Fade>
      </Modal>
    </div>
  );
}
